import { useState, useEffect } from "react";
import { Box, Paper, Title, Text, TextInput, PasswordInput, Button, Flex, Anchor, Image } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import trpc, { saveToken } from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { errorNotification } from "@/shared/utils/errorNotification";
import Cookies from "js-cookie";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";

export default function LoginPage() {
  const navigate = useNavigate();

  const [redirecting] = useState(() => !!Cookies.get(COOKIE_KEYS.token));

  useEffect(() => {
    if (!redirecting) return;
    navigate("/home", { replace: true });
  }, []);

  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const isPasswordValid = password.length >= 8;
  const isFormValid = username.trim() !== "" && isPasswordValid;

  const handleLogin = async () => {
    if (!isFormValid) return;
    setLoading(true);
    try {
      const data = await trpc.auth.login.mutate({ username, password });

      if (!data.data?.token) {
        errorNotification({ title: "Login Failed", message: "No token received from server." });
        return;
      }

      saveToken(data.data.token);
      successNotification({ title: "Login Successful!", message: "Welcome to SiP24Seven." });
      navigate("/home", { replace: true });
    } catch (err: any) {
      const code = err?.data?.code;
      if (code === "FORBIDDEN") {
        errorNotification({ title: "Login Failed", message: "Invalid username or password." });
      } else {
        handleTrpcError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isFormValid && !loading) {
      handleLogin();
    }
  };

  if (redirecting) return null;

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--mantine-color-slate-1)",
      }}
    >
      {/* Branding */}
      <Image src="/LoginPageLogo.svg" alt="SiP24Seven Logo" w={120} mb="xl" />

      {/* Card */}
      <Paper shadow="md" radius="xl" style={{ width: 720, overflow: "hidden" }}>
        <Flex>
          {/* Left: form — exactly half */}
          <Box p={40} style={{ width: "50%" }}>
            <Title order={2} fz={22} mb={8} ta="center">
              Welcome to SiP24Seven
            </Title>
            <Text fz="sm" c="slate.5" mb={28} ta="center">
              Manage users, monitor user activity, and configure the app from your admin dashboard.
            </Text>

            <TextInput
              label="Username"
              placeholder="Enter username"
              required
              mb="md"
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
            />

            <Box mb="xl" style={{ minHeight: 82 }}>
              <PasswordInput
                label="Password"
                placeholder="Enter password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                error={password && !isPasswordValid ? "Password must be at least 8 characters" : null}
              />
            </Box>

            <Button fullWidth onClick={handleLogin} loading={loading} disabled={!isFormValid || loading} mb="md">
              Login
            </Button>

            <Text fz="xs" c="slate.5" ta="center">
              In case of any problem, contact{" "}
              <Anchor fz="xs" href="mailto:support@sip24seven.com" c="primary.6" td="underline">
                SiP24Seven Support
              </Anchor>{" "}
              for assistance.
            </Text>
          </Box>

          {/* Right: image — exactly half */}
          <Box
            style={{
              width: "50%",
              background: "linear-gradient(160deg, #C5D9EE 0%, #E4EEF8 100%)",
              overflow: "hidden",
            }}
          >
            <Image src="/LoginSIP.svg" alt="SiP24Seven" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
        </Flex>
      </Paper>
    </Box>
  );
}
